import type { ConsentDecision } from './consent';
import { readCookie } from './gaCookies';

// Cookiebot loader and reader (#1458). Loaded without auto-blocking: blocking
// mode rewrites script tags and can break the Vite chunks and the WorkOS and
// HubSpot embeds. GTM itself is held back in code until consent.

export const COOKIEBOT_SCRIPT_ID = 'Cookiebot';
export const COOKIEBOT_SRC = 'https://consent.cookiebot.com/uc.js';

// Cookiebot fires OnAccept/OnDecline on every load for a returning visitor as
// well as on a fresh decision, and OnConsentReady whenever consent is known.
export const COOKIEBOT_EVENTS = [
  'CookiebotOnConsentReady',
  'CookiebotOnAccept',
  'CookiebotOnDecline',
] as const;

interface CookiebotApi {
  consent?: { statistics?: boolean; marketing?: boolean };
  hasResponse?: boolean;
  consentID?: string;
}

declare global {
  interface Window {
    Cookiebot?: CookiebotApi;
  }
}

export function loadCookiebot(cbid: string, doc: Document = document): void {
  if (doc.getElementById(COOKIEBOT_SCRIPT_ID)) {
    return;
  }
  const script = doc.createElement('script');
  script.id = COOKIEBOT_SCRIPT_ID;
  script.src = COOKIEBOT_SRC;
  script.async = true;
  script.type = 'text/javascript';
  script.setAttribute('data-cbid', cbid);
  // We map Cookiebot categories onto Consent Mode ourselves.
  script.setAttribute('data-consentmode', 'disabled');
  doc.head.appendChild(script);
}

// Cookiebot stores the decision time as `utc:<epoch ms>` inside the
// URL-encoded object it keeps in the CookieConsent cookie.
const DECISION_UTC_RE = /utc:(\d{12,14})/;

export function decisionTimeFromCookie(cookie: string): Date | undefined {
  for (const raw of readCookie(cookie, 'CookieConsent')) {
    let decoded = raw;
    try {
      decoded = decodeURIComponent(raw);
    } catch {
      // Keep the raw value.
    }
    const match = DECISION_UTC_RE.exec(decoded);
    if (match) {
      const at = new Date(Number(match[1]));
      if (!Number.isNaN(at.getTime())) {
        return at;
      }
    }
  }
  return undefined;
}

/** The current Cookiebot decision, or null before Cookiebot has loaded. */
export function readCookiebotDecision(
  win: Window = window,
  cookie: string = document.cookie,
): ConsentDecision | null {
  const cookiebot = win.Cookiebot;
  if (!cookiebot?.consent) {
    return null;
  }

  const decision: ConsentDecision = {
    hasResponse: Boolean(cookiebot.hasResponse),
    statistics: Boolean(cookiebot.consent.statistics),
    marketing: Boolean(cookiebot.consent.marketing),
  };
  if (cookiebot.consentID) {
    decision.consentId = cookiebot.consentID;
  }
  const decidedAt = decisionTimeFromCookie(cookie);
  if (decidedAt) {
    decision.decidedAt = decidedAt;
  }
  return decision;
}

export function onCookiebotEvents(listener: () => void, win: Window = window): () => void {
  for (const name of COOKIEBOT_EVENTS) {
    win.addEventListener(name, listener);
  }
  return () => {
    for (const name of COOKIEBOT_EVENTS) {
      win.removeEventListener(name, listener);
    }
  };
}
